import { Dispatch, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ApplicationStatus, DocStatus, DocTypes, TOnSignFileData,
} from 'components/DocBox/DocBox.types';
import { EXTERNAL_PRODUCTS } from 'constants/constants';
import { getBase64FromFile } from 'utils/file';
import arrayContains from 'utils/arrayUtils';
import { ApplicationDetailsActionTypes } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { updateTypeToActionMap } from 'datasource/useApplicationDetails/constants';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';

type UseApplicationActionsParams = {
    dispatch: Dispatch<any>;
    containerId?: string;
    urlApplicationId?: string;
    dfsClientId?: string;
    applicationsData: any[];
    advisoryDetailsData: any;
    getInvestmentApplications?: () => any;
    refetchAdvisory?: () => any;
};

/**
 * Bundles the imperative application/document actions: cancel (single + all), accept-send,
 * manual sign, e-sign, and advisory re-upload. Extracted verbatim from ApplicationDetailsManager
 * (dep arrays preserved). Each mutation invalidates its related queries and drives the relevant
 * refetch handle (invalidateQueries is a no-op for the `enabled: false` reads).
 */
export const useApplicationActions = ({
    dispatch,
    containerId,
    urlApplicationId,
    dfsClientId,
    applicationsData,
    advisoryDetailsData,
    getInvestmentApplications,
    refetchAdvisory,
}: UseApplicationActionsParams) => {
    const { i18n: { language } } = useTranslation();
    const { mutateAsync: handleMutation } = useGenericAPIMutation();

    const cancelAllApplications = useCallback(async () => {
        // Only container-docs is invalidated here. The apps overview is refreshed by the returned
        // `getInvestmentApplications` handle, which the caller (ApplicationDetailsHeader) invokes in
        // its finally block — invalidating 'getInvestmentApplicationOverview' too would double-fetch it.
        if (containerId) {
            await handleMutation({
                service: 'customInvestmentService',
                API: 'cancelApplicationsByContainerId',
                payload: { containerId },
                invalidateQueryKeys: ['getInvestmentApplicationOverview', 'getData_getProofOfAssetsDocs', 'getData_getCasesByContainerId'],
            });
        } else if (urlApplicationId) {
            await handleMutation({
                service: 'customInvestmentService',
                API: 'cancelApplication',
                payload: { applicationId: urlApplicationId },
                invalidateQueryKeys: ['getInvestmentApplicationOverview'],
            });
        }
    }, [containerId, urlApplicationId, handleMutation, getInvestmentApplications]);

    const cancelApplication = useCallback(async (payload: {applicationId: number}) => {
        await handleMutation({
            service: 'customInvestmentService',
            API: 'cancelApplication',
            payload,
            invalidateQueryKeys: ['getInvestmentApplicationOverview', 'getData_getDocumentsByContainer'],
        });
        dispatch({
            type: ApplicationDetailsActionTypes.UPDATE_APPLICATION_STATUS,
            payload: {
                applicationId: payload.applicationId,
                status: ApplicationStatus.canceled,
            },
        });
    }, [handleMutation]);

    const acceptDocumentSend = useCallback(async (payload: {documentId: number}) => {
        await handleMutation({
            service: 'customDocumentsService',
            API: 'postDocumentSentFlag',
            payload,
        });
        dispatch({
            type: ApplicationDetailsActionTypes.UPDATE_APPLICATION_DOC_WITH_TRANSFER,
            payload: {
                ...payload,
                status: DocStatus.ReadyToSubmit,
            },
        });
    }, [handleMutation]);

    const onESign = useCallback(async ({ documentId }: { documentId: number}) => {
        const response = await handleMutation({
            service: 'customDocumentsService',
            API: 'getESignUrl',
            payload: {
                documentId,
                returnUrl: `${window.location.origin}${window.location.pathname}`,
                language,
            },
        });

        window.open((response as any)?.data?.signingUrl, '_self');
    }, [dfsClientId, language, handleMutation]);

    const onManualSign = useCallback(async ({
        file,
        contactId,
        originOfAsset,
        documentId,
        type,
    }: TOnSignFileData) => {
        const base64File = await getBase64FromFile(file as File);
        const splitted = base64File.split(',');

        if (type === DocTypes.PROOF_OF_ASSETS) {
            const payload = {
                containerId,
                contactId,
                originOfAsset,
                document: {
                    filename: (file as File)?.name,
                    file: splitted[1],
                    documentId,
                },
            };

            const response = await handleMutation({
                service: 'customDocumentsService',
                API: 'uploadProofOfAssetsDocuments',
                payload,
            });
            const data = (response as any)?.data;

            dispatch({
                type: ApplicationDetailsActionTypes.UPDATE_PROOF_OF_ASSETS_DOC,
                payload: {
                    documentId: data?.documentId,
                    originOfAsset,
                    contactId,
                },
            });
        } else if (type === DocTypes.BASIC_AGREEMENT || type === DocTypes.APPLICATION) {
            let is3bDoc = false;

            if (type === DocTypes.APPLICATION) {
                is3bDoc = applicationsData.some(
                    ({ productExternalId }) => (
                        !arrayContains([EXTERNAL_PRODUCTS.aaa, EXTERNAL_PRODUCTS.aaap, EXTERNAL_PRODUCTS.fz, EXTERNAL_PRODUCTS.fzp], productExternalId)
                    ),
                );
            }

            const payload = {
                file: splitted[1],
                documentId,
                ...{ isNewSigningFlow: is3bDoc ? true : undefined },
            };

            await handleMutation({
                service: 'customDocumentsService',
                API: 'manualDocumentSign',
                payload,
            });

            dispatch({
                type: updateTypeToActionMap[type],
                payload: {
                    documentId,
                },
            });
        }
    }, [applicationsData, handleMutation, containerId]);

    const onAdvisoryDocReUpload = useCallback(async ({ filename, file }) => {
        if (containerId && advisoryDetailsData?.id) {
            const base64File = await getBase64FromFile(file as File);
            const splitted = base64File.split(',');

            const result = await handleMutation({
                service: 'customDocumentsService',
                API: 'reUploadAdvisoryDocument',
                payload: {
                    filename,
                    file: splitted?.[1],
                    containerId,
                    advisoryId: advisoryDetailsData?.id,
                },
            });

            return result;
        }

        return undefined;
    }, [containerId, advisoryDetailsData?.id, handleMutation, refetchAdvisory]);

    return {
        cancelAllApplications,
        cancelApplication,
        acceptDocumentSend,
        onESign,
        onManualSign,
        onAdvisoryDocReUpload,
    };
};
